import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const CartContext = createContext();

// Detect if we're on Emergent preview (use backend) or elsewhere like Netlify (use local /api)
const isEmergentPreview = typeof window !== 'undefined' && window.location.hostname.includes('emergentagent.com');
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Only use backend URL on Emergent preview, otherwise use relative /api path for Netlify Functions
const API = (isEmergentPreview && BACKEND_URL) ? `${BACKEND_URL}/api` : '/api';

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCartId = localStorage.getItem('wildones_cart_id');
    if (savedCartId) {
      fetchCart(savedCartId);
    }
  }, []);

  const fetchCart = async (cartId) => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/cart/${encodeURIComponent(cartId)}`);
      setCart(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching cart:', err);
      // Cart might be expired, clear it
      localStorage.removeItem('wildones_cart_id');
      setCart(null);
    } finally {
      setIsLoading(false);
    }
  };

  const createCart = async (variantId, quantity = 1) => {
    try {
      setIsLoading(true);
      const response = await axios.post(`${API}/cart/create`, {
        lines: [{ merchandiseId: variantId, quantity }]
      });
      const newCart = response.data;
      setCart(newCart);
      localStorage.setItem('wildones_cart_id', newCart.id);
      setError(null);
      return newCart;
    } catch (err) {
      console.error('Error creating cart:', err);
      setError('Failed to create cart');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const addToCart = useCallback(async (variantId, quantity = 1) => {
    try {
      setIsLoading(true);
      
      if (!cart) {
        const newCart = await createCart(variantId, quantity);
        setIsCartOpen(true);
        return newCart;
      }

      const response = await axios.post(`${API}/cart/add`, {
        cartId: cart.id,
        lines: [{ merchandiseId: variantId, quantity }]
      });
      setCart(response.data);
      setIsCartOpen(true);
      setError(null);
      return response.data;
    } catch (err) {
      console.error('Error adding to cart:', err);
      setError('Failed to add item to cart');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [cart]);

  const updateCartLine = async (lineId, quantity) => {
    if (!cart) return;

    try {
      setIsLoading(true);
      const response = await axios.post(`${API}/cart/update`, {
        cartId: cart.id,
        lines: [{ merchandiseId: lineId, quantity }]
      });
      setCart(response.data);
      setError(null);
    } catch (err) {
      console.error('Error updating cart:', err);
      setError('Failed to update cart');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const removeFromCart = async (lineId) => {
    if (!cart) return;

    try {
      setIsLoading(true);
      const response = await axios.post(`${API}/cart/remove`, {
        cartId: cart.id,
        lineIds: [lineId]
      });
      setCart(response.data);
      setError(null);
    } catch (err) {
      console.error('Error removing from cart:', err);
      setError('Failed to remove item');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const clearCart = () => {
    setCart(null);
    localStorage.removeItem('wildones_cart_id');
  };

  const cartCount = cart?.lines?.reduce((sum, line) => sum + line.quantity, 0) || 0;

  return (
    <CartContext.Provider
      value={{
        cart,
        isCartOpen,
        setIsCartOpen,
        isLoading,
        error,
        addToCart,
        updateCartLine,
        removeFromCart,
        clearCart,
        cartCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
